import { useState } from 'react'
import ProfessionalList from './ProfessionalList'

export default function ChatMessages({ messages, messagesEndRef }) {
  const [fullscreenGallery, setFullscreenGallery] = useState(null) // { images: [], currentIndex: 0 }
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)

  // Swipe handlers
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!fullscreenGallery) return
    
    const swipeDistance = touchStart - touchEnd
    const minSwipeDistance = 50

    if (swipeDistance > minSwipeDistance) {
      // Swiped left - next image
      goToNext()
    } else if (swipeDistance < -minSwipeDistance) {
      // Swiped right - previous image
      goToPrevious()
    }
  }

  const goToNext = () => {
    if (!fullscreenGallery) return
    const nextIndex = (fullscreenGallery.currentIndex + 1) % fullscreenGallery.images.length
    setFullscreenGallery({ ...fullscreenGallery, currentIndex: nextIndex })
  }

  const goToPrevious = () => {
    if (!fullscreenGallery) return
    const prevIndex = (fullscreenGallery.currentIndex - 1 + fullscreenGallery.images.length) % fullscreenGallery.images.length
    setFullscreenGallery({ ...fullscreenGallery, currentIndex: prevIndex })
  }

  const openGallery = (images, index) => {
    setFullscreenGallery({ images, currentIndex: index })
  }

  const closeGallery = () => {
    setFullscreenGallery(null)
  }

  if (messages.length === 0) return null

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {messages.map(msg => (
          <div key={msg.id} className="mb-6">
            <div className={`flex gap-4 ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
              {/* Avatar */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                msg.sender === 'user' 
                  ? 'bg-gray-900 text-white' 
                  : 'bg-green-500 text-white'
              }`}>
                {msg.sender === 'user' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                )}
              </div>
              
              {/* Message Content */}
              <div className={`flex-1 ${msg.sender === 'user' ? 'text-right' : ''}`}>
                {/* Image Thumbnails */}
                {msg.type === 'images' && msg.images && (
                  <div className={`inline-block ${msg.sender === 'user' ? 'text-right' : 'text-left'} mb-2`}>
                    <div className="flex flex-wrap gap-2 max-w-md">
                      {msg.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.dataUrl}
                            alt={img.name}
                            onClick={() => openGallery(msg.images, idx)}
                            className="w-24 h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border-2 border-gray-200"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center">
                            <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M15 3h6v6h-2V5h-4V3zM9 3H3v6h2V5h4V3zm6 18h6v-6h-2v4h-4v2zM9 21H3v-6h2v4h4v2z"/>
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{msg.text}</div>
                  </div>
                )}

                {/* Professional Matching Results */}
                {(msg.type === 'professionals' || msg.type === 'professional_cards') && msg.professionals && (
                  <div className={`inline-block w-full ${msg.sender === 'user' ? 'text-right' : 'text-left'}`}>
                    <ProfessionalList
                      professionals={msg.professionals}
                      recommendation={msg.recommendation}
                      criteria={msg.criteria}
                      searchQuery={msg.searchQuery}
                      noResults={msg.noResults}
                    />
                  </div>
                )}

                {/* Regular Text Message */}
                {msg.type !== 'images' && msg.type !== 'professionals' && msg.type !== 'professional_cards' && (
                  <div className={`inline-block max-w-full ${
                    msg.type === 'emergency'
                      ? 'bg-red-50 border border-red-200 text-red-900 p-4 rounded-xl'
                      : msg.type === 'system'
                      ? 'bg-blue-50 text-blue-900 p-3 rounded-xl text-sm'
                      : msg.sender === 'user'
                      ? 'bg-gray-900 text-white p-4 rounded-xl'
                      : 'text-gray-900'
                  }`}>
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {msg.text}
                    </div>
                  </div>
                )}
                
                <div className="text-xs text-gray-500 mt-2">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Fullscreen Image Carousel */}
      {fullscreenGallery && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close Button */}
          <button
            onClick={closeGallery}
            className="absolute top-6 right-6 z-10 text-white hover:text-gray-300 transition-colors"
          >
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Image Counter */}
          <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-full">
            {fullscreenGallery.currentIndex + 1} / {fullscreenGallery.images.length}
          </div>

          {/* Previous Button (Desktop + Mobile) */}
          {fullscreenGallery.images.length > 1 && (
            <button
              onClick={goToPrevious}
              className="absolute left-4 z-10 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-3"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Current Image */}
          <img
            src={fullscreenGallery.images[fullscreenGallery.currentIndex].dataUrl}
            alt={fullscreenGallery.images[fullscreenGallery.currentIndex].name}
            className="w-full h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next Button (Desktop + Mobile) */}
          {fullscreenGallery.images.length > 1 && (
            <button
              onClick={goToNext}
              className="absolute right-4 z-10 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-3"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Thumbnail Strip (Bottom) */}
          {fullscreenGallery.images.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 z-10 flex justify-center gap-2 px-4 overflow-x-auto">
              {fullscreenGallery.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setFullscreenGallery({ ...fullscreenGallery, currentIndex: idx })}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === fullscreenGallery.currentIndex 
                      ? 'border-white scale-110' 
                      : 'border-gray-500 opacity-60'
                  }`}
                >
                  <img
                    src={img.dataUrl}
                    alt={img.name}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
